import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { NewsArticle } from '@/types/news';
import { formatViews, getCategoryById } from '@/utils/newsUtils';

interface HeroCarouselProps {
  items: NewsArticle[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="w-full h-[320px] md:h-[440px] rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 text-sm">
        暂无最新新闻，敬请期待
      </div>
    );
  }

  const prev = () => setIndex((i) => (i - 1 + items.length) % items.length);
  const next = () => setIndex((i) => (i + 1) % items.length);

  return (
    <section className="relative w-full h-[340px] md:h-[440px] rounded-lg overflow-hidden bg-foreground-950">
      {items.map((item, i) => {
        const category = getCategoryById(item.categoryId);
        return (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} - 吃瓜网焦点新闻`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10"></div>
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-8 w-full">
              <div className="max-w-3xl">
                {category && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-primary-50 whitespace-nowrap">
                    <i className={category.icon}></i>
                    {category.name}
                  </span>
                )}
                <h2 className="mt-3 text-xl md:text-3xl font-bold font-heading text-white leading-snug line-clamp-2">
                  {item.title}
                </h2>
                <p className="mt-2 text-sm md:text-base text-white/80 leading-relaxed line-clamp-2 hidden sm:block">
                  {item.summary}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <Link
                    to={`/detail/${item.id}`}
                    className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-primary-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    阅读全文
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <span className="flex items-center gap-3 text-xs text-white/70">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {item.publishedAt}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatViews(item.views)}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={prev}
        aria-label="上一条"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/35 text-white items-center justify-center hover:bg-black/55 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="下一条"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/35 text-white items-center justify-center hover:bg-black/55 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute top-4 right-4 md:top-auto md:bottom-8 md:left-8 md:right-auto flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`第 ${i + 1} 张`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? 'w-6 bg-primary-500' : 'w-1.5 bg-white/50 hover:bg-white/80'}`}
          ></button>
        ))}
      </div>
    </section>
  );
}